import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Checkbox,
  Tooltip,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import icon_info from "assets/images/icons/icn_Info_solid.svg";
import icon_info_hover from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import ModalContainer from "components/ModalContainer";
import TimePicker from "components/TimePicker";
import SearchInput from "components/SearchInput";
import { RadioButton } from "components/buttons/RadioButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { WEEKDAYS, DEFAULT_RULE } from "../const";

const CreateRuleModal = ({
  open,
  onClose,
  onApply,
  showEditGeneral = true,
  devicesList = [],
}) => {
  const theme = useTheme();

  const [name, setName] = useState(DEFAULT_RULE.name);
  const [enable, setEnable] = useState(DEFAULT_RULE.enable);
  const [applyType, setApplyType] = useState("models"); // "models" or "devices"
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [frequencyType, setFrequencyType] = useState("Daily");
  const [selectedDays, setSelectedDays] = useState([]);
  const [time, setTime] = useState(DEFAULT_RULE.time);

  const [modelSearch, setModelSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceCategoryTab, setDeviceCategoryTab] = useState("All");

  useEffect(() => {
    if (open) {
      setName(DEFAULT_RULE.name);
      setEnable(DEFAULT_RULE.enable);
      setApplyType("models");
      setSelectedModels([]);
      setSelectedDevices([]);
      setFrequencyType("Daily");
      setSelectedDays([]);
      setTime(DEFAULT_RULE.time);
      setModelSearch("");
      setDeviceSearch("");
      setDeviceCategoryTab("All");
    }
  }, [open]);

  const processedModels = useMemo(() => {
    const modelsMap = {};
    devicesList.forEach((dev) => {
      const devInfo = dev.info || {};
      const model = devInfo.modelName || "Unknown";
      const isOnline = devInfo.linkStatus === "online";
      if (!modelsMap[model]) {
        modelsMap[model] = { name: model, total: 0, online: 0 };
      }
      modelsMap[model].total += 1;
      if (isOnline) {
        modelsMap[model].online += 1;
      }
    });
    return Object.values(modelsMap);
  }, [devicesList]);

  const filteredModels = useMemo(() => {
    return processedModels.filter((m) =>
      m.name.toLowerCase().includes(modelSearch.toLowerCase())
    );
  }, [processedModels, modelSearch]);

  const filteredDevices = useMemo(() => {
    return devicesList.filter((dev) => {
      const devInfo = dev.info || {};
      const category = devInfo.deviceType || "";
      const model = devInfo.modelName || "";
      const isAP = category === "ap" || model.startsWith("DAP") || model.startsWith("AP");
      const isSwitch = category === "switch" || model.startsWith("DGS") || model.startsWith("DXS") || model.startsWith("DES");

      if (deviceCategoryTab === "Access Point" && !isAP) return false;
      if (deviceCategoryTab === "Switch" && !isSwitch) return false;

      const nameMatch = (devInfo.deviceName || "").toLowerCase().includes(deviceSearch.toLowerCase());
      const macMatch = (devInfo.macAddress || "").toLowerCase().includes(deviceSearch.toLowerCase());

      return nameMatch || macMatch;
    });
  }, [devicesList, deviceSearch, deviceCategoryTab]);

  const handleModelToggle = (modelName) => {
    setSelectedModels((prev) =>
      prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName]
    );
  };

  const handleDeviceToggle = (mac) => {
    setSelectedDevices((prev) =>
      prev.includes(mac) ? prev.filter((d) => d !== mac) : [...prev, mac]
    );
  };

  const handleDayToggle = (dayVal) => {
    setSelectedDays((prev) =>
      prev.includes(dayVal)
        ? prev.filter((d) => d !== dayVal)
        : [...prev, dayVal]
    );
  };

  const checkFormValid = () => {
    if (!name.trim()) return false;
    if (applyType === "models" && selectedModels.length === 0) return false;
    if (applyType === "devices" && selectedDevices.length === 0) return false;
    if (frequencyType === "Weekly" && selectedDays.length === 0) return false;
    return true;
  };

  const handleSave = () => {
    if (!checkFormValid()) return;

    let freqValue = "daily";
    if (frequencyType === "Weekly") {
      freqValue = selectedDays.sort((a, b) => a - b).join(",");
    }

    let finalDeviceIds = [];
    if (applyType === "models") {
      finalDeviceIds = devicesList
        .filter((d) => d.info && selectedModels.includes(d.info.modelName))
        .map((d) => d.info.macAddress)
        .filter(Boolean);
    } else {
      finalDeviceIds = selectedDevices;
    }

    const payload = {
      name,
      enable,
      modelNames: applyType === "models" ? selectedModels : [],
      deviceIds: finalDeviceIds,
      frequency: freqValue,
      time,
    };

    onApply(payload);
  };

  // Styled Form layouts defined as local constants to keep JSX DOM clean
  const rowStyle = {
    paddingLeft: "32px",
    marginTop: "20px",
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
  };

  const labelStyle = {
    width: "180px",
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
    paddingTop: "6px",
    flexShrink: 0,
  };

  const textfieldStyle = {
    width: "286px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "30px",
      "& fieldset": {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.mainHover,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const tabOuterContainerStyle = {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    maxWidth: "500px",
  };

  const pillContainerStyle = {
    display: "flex",
    border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
    borderRadius: "4px",
    overflow: "hidden",
    height: "26px",
    alignItems: "center",
    width: "fit-content",
  };

  const getPillItemStyle = (isActive) => ({
    px: 3,
    height: "100%",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    cursor: showEditGeneral ? "pointer" : "not-allowed",
    backgroundColor: isActive ? theme.palette.primary.main : "#fff",
    color: isActive ? "#fff" : theme.palette.primary.greySecound,
    userSelect: "none",
    transition: "background-color 0.2s, color 0.2s",
  });

  const tabItemUnderlineStyle = {
    display: "flex",
    borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
    mb: 2,
  };

  const getTabItemStyle = (isActive) => ({
    px: 3,
    py: 1,
    cursor: showEditGeneral ? "pointer" : "not-allowed",
    fontWeight: isActive ? "bold" : "normal",
    borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : "none",
    color: isActive ? theme.palette.primary.main : theme.palette.primary.greySecound,
    fontSize: "13px",
    userSelect: "none",
  });

  const getWeekdayPillStyle = (isSelected) => ({
    paddingTop: "4px",
    paddingBottom: "4px",
    width: "60px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "16px",
    fontSize: "13px",
    cursor: showEditGeneral ? "pointer" : "not-allowed",
    border: `1px solid ${isSelected ? theme.palette.primary.main : "#cccccc"}`,
    backgroundColor: isSelected ? theme.palette.primary.main : "#ffffff",
    color: isSelected ? "#ffffff" : theme.palette.primary.greyPrimary,
    userSelect: "none",
    transition: "all 0.2s ease",
  });

  const listContainerStyle = {
    border: `1px solid ${theme.palette.primary.greyThread}`,
    borderRadius: "4px",
    maxHeight: "220px",
    overflowY: "auto",
    overflowX: "auto",
    mt: 1,
  };

  const actionButtonsContainerStyle = {
    margin: "50px auto 30px",
    display: "flex",
    justifyContent: "center",
    gap: "20px",
  };

  return (
    <ModalContainer display={open} modalWidth="850px">
      <Box sx={{ padding: "0 10px" }}>
        {/* Header Section matching SSID Style */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Create Schedule Rule
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              minWidth: 0,
              padding: 0,
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        {/* Content Form - Natural layout flow, no section separators, matching wireframe Figure 1 */}
        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>

          {/* 1. Rule Name */}
          <Box sx={rowStyle}>
            <Box sx={labelStyle}>Rule Name</Box>
            <TextField
              placeholder="e.g. Scheduled Reboot AP"
              variant="outlined"
              size="small"
              disabled={!showEditGeneral}
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={textfieldStyle}
              autoComplete="off"
            />
          </Box>

          {/* 2. Apply to */}
          <Box sx={rowStyle}>
            <Box sx={labelStyle}>Apply to</Box>
            <Box sx={tabOuterContainerStyle}>
              {/* Tab Switchers */}
              <Box sx={tabItemUnderlineStyle}>
                <Box
                  onClick={() => showEditGeneral && setApplyType("models")}
                  sx={getTabItemStyle(applyType === "models")}
                >
                  Device Models
                </Box>
                <Box
                  onClick={() => showEditGeneral && setApplyType("devices")}
                  sx={getTabItemStyle(applyType === "devices")}
                >
                  Specific Devices
                </Box>
              </Box>

              {/* Device Models List Content */}
              {applyType === "models" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <SearchInput
                      value={modelSearch}
                      onChange={setModelSearch}
                      placeholder="Search by model name"
                    />
                    <OutlineButton
                      height="30px"
                      disabled={!showEditGeneral}
                      onClick={() => setSelectedModels([])}
                    >
                      Clear Selections
                    </OutlineButton>
                  </Box>

                  <Box sx={listContainerStyle}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#fafafa", borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                          <th style={{ padding: "8px", width: "40px", textAlign: "center" }}>
                            <Checkbox
                              disabled={!showEditGeneral}
                              checked={filteredModels.length > 0 && filteredModels.every((m) => selectedModels.includes(m.name))}
                              indeterminate={
                                filteredModels.some((m) => selectedModels.includes(m.name)) &&
                                !filteredModels.every((m) => selectedModels.includes(m.name))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const newSels = Array.from(new Set([...selectedModels, ...filteredModels.map((m) => m.name)]));
                                  setSelectedModels(newSels);
                                } else {
                                  const filteredNames = filteredModels.map((m) => m.name);
                                  setSelectedModels(selectedModels.filter((m) => !filteredNames.includes(m)));
                                }
                              }}
                            />
                          </th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#666" }}>Model</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#666" }}>Devices Amount</th>
                          <th style={{ padding: "8px", textAlign: "right", color: "#666", paddingRight: "16px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredModels.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: "16px", color: "#999" }}>
                              No data available
                            </td>
                          </tr>
                        ) : (
                          filteredModels.map((m) => (
                            <tr key={m.name} style={{ borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                              <td style={{ padding: "4px 8px", textAlign: "center" }}>
                                <Checkbox
                                  disabled={!showEditGeneral}
                                  checked={selectedModels.includes(m.name)}
                                  onChange={() => handleModelToggle(m.name)}
                                />
                              </td>
                              <td style={{ padding: "4px 8px", textAlign: "left", fontWeight: 500 }}>{m.name}</td>
                              <td style={{ padding: "4px 8px", textAlign: "left", color: "#666" }}>{m.total} device(s)</td>
                              <td style={{ padding: "4px 8px", textAlign: "right", paddingRight: "16px", color: "#666" }}>
                                {m.online} online
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </Box>
                  <Typography sx={{ mt: 1, fontSize: "12px", color: theme.palette.primary.greySecound }}>
                    {selectedModels.length} device models selected
                  </Typography>
                </Box>
              )}

              {/* Specific Devices List Content */}
              {applyType === "devices" && (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
                    <SearchInput
                      value={deviceSearch}
                      onChange={setDeviceSearch}
                      placeholder="Search by name or MAC address"
                    />
                    <OutlineButton
                      height="30px"
                      disabled={!showEditGeneral}
                      onClick={() => setSelectedDevices([])}
                    >
                      Clear Selections
                    </OutlineButton>
                  </Box>

                  <Box sx={listContainerStyle}>
                    <table style={{ width: "100%", minWidth: "750px", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#fafafa", borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                          <th style={{ padding: "8px", width: "40px", textAlign: "center" }}>
                            <Checkbox
                              disabled={!showEditGeneral}
                              checked={filteredDevices.length > 0 && filteredDevices.every((d) => selectedDevices.includes(d.info?.macAddress))}
                              indeterminate={
                                filteredDevices.some((d) => selectedDevices.includes(d.info?.macAddress)) &&
                                !filteredDevices.every((d) => selectedDevices.includes(d.info?.macAddress))
                              }
                              onChange={(e) => {
                                const devMacs = filteredDevices.map((d) => d.info?.macAddress).filter(Boolean);
                                if (e.target.checked) {
                                  setSelectedDevices(Array.from(new Set([...selectedDevices, ...devMacs])));
                                } else {
                                  setSelectedDevices(selectedDevices.filter((d) => !devMacs.includes(d)));
                                }
                              }}
                            />
                          </th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#666" }}>Device Name</th>
                          <th style={{ padding: "8px", textAlign: "center", color: "#666" }}>Access Point</th>
                          <th style={{ padding: "8px", textAlign: "center", color: "#666" }}>Switch</th>
                          <th style={{ padding: "8px", textAlign: "left", color: "#666" }}>MAC Address</th>
                          <th style={{ padding: "8px", textAlign: "right", color: "#666", paddingRight: "16px" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDevices.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: "center", padding: "16px", color: "#999" }}>
                              No data available
                            </td>
                          </tr>
                        ) : (
                          filteredDevices.map((d) => {
                            const devInfo = d.info || {};
                            const mac = devInfo.macAddress;
                            const nameVal = devInfo.deviceName || "Device";
                            const isOnline = devInfo.linkStatus === "online";
                            const model = devInfo.modelName || "";
                            const category = devInfo.deviceType || "";
                            const isAP = category === "ap" || model.startsWith("DAP") || model.startsWith("AP");
                            const isSwitch = category === "switch" || model.startsWith("DGS") || model.startsWith("DXS") || model.startsWith("DES");

                            return (
                              <tr key={mac} style={{ borderBottom: `1px solid ${theme.palette.primary.greyThread}` }}>
                                <td style={{ padding: "4px 8px", textAlign: "center" }}>
                                  <Checkbox
                                    disabled={!showEditGeneral}
                                    checked={selectedDevices.includes(mac)}
                                    onChange={() => handleDeviceToggle(mac)}
                                  />
                                </td>
                                <td style={{ padding: "4px 8px", textAlign: "left", fontWeight: 500 }}>{nameVal}</td>
                                <td style={{ padding: "4px 8px", textAlign: "center", color: theme.palette.primary.main, fontWeight: "bold" }}>
                                  {isAP ? "✓" : ""}
                                </td>
                                <td style={{ padding: "4px 8px", textAlign: "center", color: theme.palette.primary.main, fontWeight: "bold" }}>
                                  {isSwitch ? "✓" : ""}
                                </td>
                                <td style={{ padding: "4px 8px", textAlign: "left", color: "#666" }}>{mac}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right", paddingRight: "16px" }}>
                                  <span style={{ color: "#666" }}>
                                    {isOnline ? "Online" : "Offline"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </Box>
                  <Typography sx={{ mt: 1, fontSize: "12px", color: theme.palette.primary.greySecound }}>
                    {selectedDevices.length} devices selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* 3. Frequency */}
          <Box sx={rowStyle}>
            <Box sx={labelStyle}>Frequency</Box>
            <Box sx={tabOuterContainerStyle}>
              <Box sx={pillContainerStyle}>
                <Box
                  onClick={() => showEditGeneral && setFrequencyType("Daily")}
                  sx={getPillItemStyle(frequencyType === "Daily")}
                >
                  Daily
                </Box>
                <Box
                  onClick={() => showEditGeneral && setFrequencyType("Weekly")}
                  sx={getPillItemStyle(frequencyType === "Weekly")}
                >
                  Weekly
                </Box>
              </Box>

              {/* Weekdays selection rendering below pill container */}
              {frequencyType === "Weekly" && (
                <Grid container spacing={1} sx={{ maxWidth: "450px", mt: 1.5 }}>
                  {WEEKDAYS.map((day) => {
                    const isSelected = selectedDays.includes(day.value);
                    return (
                      <Grid item key={day.value}>
                        <Box
                          onClick={() => showEditGeneral && handleDayToggle(day.value)}
                          sx={getWeekdayPillStyle(isSelected)}
                        >
                          {day.label}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>

          {/* 4. Time */}
          <Box sx={rowStyle}>
            <Box sx={{ ...labelStyle, display: "flex", alignItems: "center", gap: 0.5, paddingTop: "4px" }}>
              <span>Time</span>
              <Tooltip title="Controller's local time zone." placement="bottom" arrow>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundImage: `url(${icon_info})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundImage: `url(${icon_info_hover})`,
                    }
                  }}
                />
              </Tooltip>
            </Box>
            <TimePicker
              value={time}
              onChange={setTime}
              disabled={!showEditGeneral}
            />
          </Box>

          {/* 5. Status - Using RadioButton components matching wireframe Figure 1 */}
          <Box sx={rowStyle}>
            <Box sx={labelStyle}>Status</Box>
            <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <RadioButton
                label="Enable"
                value="enable"
                checked={enable === true}
                onChange={() => showEditGeneral && setEnable(true)}
                disabled={!showEditGeneral}
              />
              <RadioButton
                label="Disable"
                value="disable"
                checked={enable === false}
                onChange={() => showEditGeneral && setEnable(false)}
                disabled={!showEditGeneral}
              />
            </Box>
          </Box>

        </Box>

        {/* Action Buttons Section matching SSID Modal Style */}
        {showEditGeneral && (
          <Box sx={actionButtonsContainerStyle}>
            <OutlineButton
              onClick={onClose}
              width="100px"
              height="32px"
            >
              Cancel
            </OutlineButton>
            <SolidButton
              disabled={!checkFormValid()}
              onClick={handleSave}
              width="100px"
              height="32px"
            >
              Create
            </SolidButton>
          </Box>
        )}
      </Box>
    </ModalContainer>
  );
};

export default CreateRuleModal;
